import {
  enums,
  IPriceAlert,
  IPriceAlertCreatePayload,
  IPriceAlertUpdatePayload,
  IResponse,
} from '@kattana-io/common';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { LOG_OUT, SET_AUTH } from '../actions/auth';
import { actionCreateNotification } from '../actions/notifications';
import {
  ACTION_CREATE_PRICE_ALERT,
  ACTION_CONSUME_ALERT,
  ACTION_GET_PRICE_ALERTS,
  ACTION_CANCEL_ALERT,
  ACTION_DUPLICATE_PRICE_ALERT,
  ACTION_UPDATE_PRICE_ALERT_PRICE,
  actionSetPriceAlerts,
  actionGetPriceAlerts,
  actionPutPriceAlert,
  actionAddPriceAlert,
  actionSetCreatingPriceAlerts,
} from '../actions/price_alerts';
import { IStore } from '../reducers';
import { authTokenSelector } from '../selectors/auth';
import { PriceAlertAPI } from '../store/createStore';

type GetPriceAlertsPayload = string;

function* onGetPriceAlerts(
  action: PayloadAction<any, GetPriceAlertsPayload>
): any {
  const wallet = action.payload;

  try {
    const token = yield select(authTokenSelector);
    const res: IResponse<IPriceAlert[]> = yield call(
      PriceAlertAPI.getPriceAlerts,
      wallet,
      token
    );
    if (res.success && res.data) {
      yield put(actionSetPriceAlerts(res.data));
    } else {
      console.warn('Could not load price alerts');
      yield put(actionSetPriceAlerts([]));
    }
  } catch (err) {
    yield put(actionSetPriceAlerts([]));
    console.error(err);
  }
}

function* onCreatePriceAlert(
  action: PayloadAction<any, IPriceAlertCreatePayload>
): any {
  try {
    yield put(actionSetCreatingPriceAlerts(true));
    const token = yield select(authTokenSelector);
    const res: IResponse<IPriceAlert> = yield call(
      PriceAlertAPI.createPriceAlert,
      action.payload,
      token
    );

    if (res.success && res.data) {
      yield put(actionAddPriceAlert(res.data));
    } else {
      const reason = res.error?.response?.data?.error;

      if (res.error?.response?.status === 401) {
        yield put(
          actionCreateNotification({
            kind: 'warning',
            message: 'Permission denied',
          })
        );
      } else {
        const message = reason ? reason : 'Not enough KTN amount';
        yield put(
          actionCreateNotification({
            kind: 'warning',
            message,
          })
        );
      }
    }
  } catch (error) {
    yield put(actionCreateNotification({ kind: 'warning', message: error }));
    console.error(error);
  } finally {
    yield put(actionSetCreatingPriceAlerts(false));
  }
}

function* onCopyPriceAlert(
  action: PayloadAction<any, Partial<IPriceAlert>>
): any {
  try {
    const token = yield select(authTokenSelector);
    const newAlert = Object.assign({}, action.payload);
    delete newAlert.id;
    delete newAlert.status;
    delete newAlert.uuid;

    const res: IResponse<IPriceAlert> = yield call(
      PriceAlertAPI.createPriceAlert,
      newAlert as IPriceAlertCreatePayload,
      token
    );

    if (res.success && res.data) {
      yield put(actionAddPriceAlert(res.data));
    } else {
      yield put(
        actionCreateNotification({
          kind: 'warning',
          message: 'Could not copy price alert',
        })
      );
    }
  } catch (error) {
    yield put(actionCreateNotification({ kind: 'warning', message: error }));
    console.error(error);
  }
}

function* onCancelPriceAlert(action: PayloadAction<any, IPriceAlert>): any {
  try {
    const token = yield select(authTokenSelector);
    const { payload } = action;
    const body = {
      status: enums.PriceAlertStatus.CANCELED,
    } as IPriceAlertUpdatePayload;
    const res: any = yield call(
      PriceAlertAPI.deletePriceALert,
      payload.uuid,
      payload.wallet,
      token
    );
  } catch (error) {
    console.error(error);
  }
}

function* onConsumePriceAlert(action: PayloadAction<any, IPriceAlert>): any {
  try {
    const token = yield select(authTokenSelector);
    const { payload } = action;
    const body = {
      status: enums.PriceAlertStatus.CONSUMED,
    } as IPriceAlertUpdatePayload;
    const res: any = yield call(
      PriceAlertAPI.updatePriceAlert,
      payload.uuid,
      payload.wallet,
      body,
      token
    );
  } catch (error) {
    console.error(error);
  }
}

function* onUpdatePriceAlertPrice(
  action: PayloadAction<any, IPriceAlert>
): any {
  try {
    const token = yield select(authTokenSelector);
    const { payload } = action;
    const body = {
      price: payload.price,
      price_usd: payload.price_usd,
    } as IPriceAlertUpdatePayload;
    const res = yield call(
      PriceAlertAPI.updatePriceAlert,
      payload.uuid,
      payload.wallet,
      body,
      token
    );

    if (res.success) {
      yield put(actionPutPriceAlert(payload));
    } else {
      yield put(
        actionCreateNotification({
          kind: 'warning',
          message: 'Could not update price alert',
        })
      );
    }
  } catch (error) {
    yield put(actionCreateNotification({ kind: 'warning', message: error }));
    console.error(error);
  }
}

function* onAuth(): any {
  try {
    const { wallet } = yield select((store: IStore) => store.auth);
    yield put(actionGetPriceAlerts(wallet));
  } catch (error) {
    console.error(error);
  }
}

function* onLogOut(): any {
  try {
    yield put(actionSetPriceAlerts([]));
  } catch (error) {
    console.error(error);
  }
}

export default function* priceAlertsSaga() {
  yield takeLatest(SET_AUTH, onAuth);
  yield takeLatest(LOG_OUT, onLogOut);
  yield takeLatest(ACTION_GET_PRICE_ALERTS, onGetPriceAlerts);
  yield takeLatest(ACTION_DUPLICATE_PRICE_ALERT, onCopyPriceAlert);
  yield takeLatest(ACTION_CREATE_PRICE_ALERT, onCreatePriceAlert);
  yield takeLatest(ACTION_CONSUME_ALERT, onConsumePriceAlert);
  yield takeLatest(ACTION_CANCEL_ALERT, onCancelPriceAlert);
  yield takeLatest(ACTION_UPDATE_PRICE_ALERT_PRICE, onUpdatePriceAlertPrice);
}
